import {
  AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  sortReportRows,
} from "./renderConfig";
import { buildCsv } from "./agentServiceTimeExport";

const SUMMARY_HEADERS = Object.freeze([
  "Branch",
  "Agent",
  "Total",
  "%",
  "Average",
  "Min",
  "Max",
  "Completed",
  "Completed %",
  "No Show",
  "No Show %",
  "Unattended",
  "Unattended %",
]);

const DAILY_HEADERS = Object.freeze([
  "Branch",
  "Agent",
  "Day",
  "Total",
  "Completed",
  "No Show",
  "Unattended",
]);

const toPrimitiveNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getPointDate = (point) => point.date ?? point.day ?? point.name ?? point.hour ?? "";
const getPoints = (row) => row.charts || row.chart || [];
const pointLabelPrefix = (rows) =>
  rows.some((row) => getPoints(row).some((point) => point?.hour !== undefined && point?.hour !== null))
    ? "Hour"
    : "Day";

export const getTicketsPerAgentRows = (branch) => {
  const totalBranchTickets = (branch.agentsData || []).reduce(
    (sum, agent) => sum + toPrimitiveNumber(agent.total),
    0
  );
  return (branch.agentsData || [])
    .filter((agent) => toPrimitiveNumber(agent?.total))
    .map((agent) => ({
      ...agent,
      branchName: branch.branchName || "",
      totalPct: totalBranchTickets ? (toPrimitiveNumber(agent.total) / totalBranchTickets) * 100 : 0,
    }));
};

export const getTicketsPerAgentRowsByBranch = (branch, renderConfig) => {
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  return sortReportRows(getTicketsPerAgentRows(branch), config, {
    columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  });
};

export const getTicketsPerAgentSummaryCsvRows = (branches = [], renderConfig) =>
  branches.flatMap((branch) =>
    getTicketsPerAgentRowsByBranch(branch, renderConfig).map((row) => [
      row.branchName || "",
      row.agentName || "",
      toPrimitiveNumber(row.total),
      toPrimitiveNumber(row.totalPct),
      toPrimitiveNumber(row.average),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
      toPrimitiveNumber(row.numCompleted),
      toPrimitiveNumber(row.pctCompleted),
      toPrimitiveNumber(row.numNoShow),
      toPrimitiveNumber(row.pctNoShow),
      toPrimitiveNumber(row.numUnattended),
      toPrimitiveNumber(row.pctUnattended),
    ])
  );

export const getTicketsPerAgentDailyCsvRows = (branches = [], renderConfig) =>
  branches.flatMap((branch) =>
    getTicketsPerAgentRowsByBranch(branch, renderConfig).flatMap((row) =>
      getPoints(row).map((point) => [
        row.branchName || "",
        row.agentName || "",
        getPointDate(point),
        toPrimitiveNumber(point.total),
        toPrimitiveNumber(point.completed),
        toPrimitiveNumber(point.no_show),
        toPrimitiveNumber(point.unattended),
      ])
    )
  );

const getDayLabels = (rows) =>
  Array.from(
    new Set(
      rows.flatMap((row) =>
        getPoints(row)
          .map(getPointDate)
          .filter((date) => date !== undefined && date !== null && date !== "")
      )
    )
  );

export const getTicketsPerAgentMergedCsvHeaders = (branches = [], renderConfig) => {
  const rows = branches.flatMap((branch) => getTicketsPerAgentRowsByBranch(branch, renderConfig));
  const labels = getDayLabels(rows);
  const prefix = pointLabelPrefix(rows);
  return [...SUMMARY_HEADERS, ...labels.map((day) => `${prefix} ${day}`)];
};

export const getTicketsPerAgentMergedCsvRows = (branches = [], renderConfig) => {
  const rows = branches.flatMap((branch) => getTicketsPerAgentRowsByBranch(branch, renderConfig));
  const dayLabels = getDayLabels(rows);
  return rows.map((row) => {
    const valuesByDay = Object.fromEntries(
      getPoints(row).map((point) => [getPointDate(point), toPrimitiveNumber(point.total)])
    );
    return [
      row.branchName || "",
      row.agentName || "",
      toPrimitiveNumber(row.total),
      toPrimitiveNumber(row.totalPct),
      toPrimitiveNumber(row.average),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
      toPrimitiveNumber(row.numCompleted),
      toPrimitiveNumber(row.pctCompleted),
      toPrimitiveNumber(row.numNoShow),
      toPrimitiveNumber(row.pctNoShow),
      toPrimitiveNumber(row.numUnattended),
      toPrimitiveNumber(row.pctUnattended),
      ...dayLabels.map((day) => valuesByDay[day] ?? ""),
    ];
  });
};

const triggerCsvDownload = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const downloadTicketsPerAgentCsvFiles = ({ data, renderConfig }) => {
  triggerCsvDownload(
    "branch_tickets_per_agent.csv",
    buildCsv(
      getTicketsPerAgentMergedCsvHeaders(data, renderConfig),
      getTicketsPerAgentMergedCsvRows(data, renderConfig)
    )
  );
};
